import React, { useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../services/apiClient';
import { useFetch } from '../hooks/useFetch';
import './VacationRequestForm.css';

const TIME_BLOCK_OPTIONS = ['AM', 'PM', 'FULL DAY'];

function formatValidationStatus(status) {
  return String(status || '').toLowerCase();
}

function VacationRequestForm({
  currentManager,
  onSubmitted,
  title = 'Request Time Off'
}) {
  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    timeBlock: 'FULL DAY',
    reason: ''
  });
  const [validationResult, setValidationResult] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const minDate = useMemo(() => new Date().toISOString().split('T')[0], []);

  useEffect(() => {
    setFormData({
      startDate: '',
      endDate: '',
      timeBlock: 'FULL DAY',
      reason: ''
    });
    setValidationResult(null);
    setSubmitError('');
    setSubmitMessage('');
  }, [currentManager?.id]);

  const canValidate = Boolean(currentManager?.id && formData.startDate && formData.endDate);

  const validationRequestOptions = useMemo(() => {
    if (!canValidate) {
      return undefined;
    }
    return {
      method: 'POST',
      body: JSON.stringify({
        managerId: currentManager.id,
        startDate: formData.startDate,
        endDate: formData.endDate,
        timeBlock: formData.timeBlock
      })
    };
  }, [canValidate, currentManager?.id, formData.startDate, formData.endDate, formData.timeBlock]);

  const {
    data: fetchedValidation,
    isLoading: isValidating,
    error: validationFetchError
  } = useFetch(canValidate ? '/api/events/vacation-validate' : null, {
    deps: [canValidate, currentManager?.id, formData.startDate, formData.endDate, formData.timeBlock],
    requestOptions: validationRequestOptions
  });

  useEffect(() => {
    if (!canValidate) {
      setValidationResult(null);
    } else if (fetchedValidation !== null && fetchedValidation !== undefined) {
      setValidationResult(fetchedValidation);
    } else if (validationFetchError) {
      setValidationResult(null);
    }
  }, [canValidate, fetchedValidation, validationFetchError]);

  const validationError = canValidate && validationFetchError
    ? (validationFetchError || 'Unable to validate this request right now.')
    : '';

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSubmitError('');
    setSubmitMessage('');
    setFormData(current => ({
      ...current,
      [name]: value,
      ...(name === 'startDate' && !current.endDate ? { endDate: value } : {})
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!currentManager?.id) {
      setSubmitError('Your account must be linked to a manager before you can request time off.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    setSubmitMessage('');

    try {
      const payload = await apiRequest('/api/events/vacation-request', {
        method: 'POST',
        body: JSON.stringify({
          managerId: currentManager.id,
          startDate: formData.startDate,
          endDate: formData.endDate,
          timeBlock: formData.timeBlock,
          reason: formData.reason
        })
      });

      const createdEvent = payload?.event || null;
      const returnedValidation = payload?.validation || null;
      const autoApproved = Boolean(payload?.autoApproved);
      if (returnedValidation) {
        setValidationResult(returnedValidation);
      }

      setFormData({
        startDate: '',
        endDate: '',
        timeBlock: 'FULL DAY',
        reason: ''
      });
      setSubmitMessage(
        autoApproved
          ? `Request submitted and auto-approved with status: ${createdEvent?.status || 'Approved'}.`
          : `Request submitted with status: ${createdEvent?.status || 'Pending'}.`
      );

      if (createdEvent && typeof onSubmitted === 'function') {
        onSubmitted(createdEvent);
      }
    } catch (error) {
      setSubmitError(error.message || 'Unable to submit vacation request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!currentManager) {
    return (
      <div className="vacation-form-container">
        <div className="validation-errors">
          Link this account to a manager before requesting time off.
        </div>
      </div>
    );
  }

  const normalizedValidationStatus = formatValidationStatus(validationResult?.status);

  return (
    <div className="vacation-form-container">
      <div className="form-header">
        <h3>{title}</h3>
        <p>
          Requester: <strong>{currentManager.name}</strong> ({currentManager.roleTitle || currentManager.role || 'Manager'})
        </p>
      </div>

      <form onSubmit={handleSubmit} className="vacation-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="startDate">Start Date *</label>
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={formData.startDate}
              onChange={handleInputChange}
              required
              min={minDate}
            />
          </div>

          <div className="form-group">
            <label htmlFor="endDate">End Date *</label>
            <input
              type="date"
              id="endDate"
              name="endDate"
              value={formData.endDate}
              onChange={handleInputChange}
              required
              min={formData.startDate || minDate}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="timeBlock">Time Block *</label>
          <select
            id="timeBlock"
            name="timeBlock"
            value={formData.timeBlock}
            onChange={handleInputChange}
            required
          >
            {TIME_BLOCK_OPTIONS.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          <small>Blackout and coverage rules are checked before the request is submitted.</small>
        </div>

        <div className="form-group">
          <label htmlFor="reason">Reason (Optional)</label>
          <textarea
            id="reason"
            name="reason"
            value={formData.reason}
            onChange={handleInputChange}
            placeholder="Family vacation, personal time, etc."
            rows="3"
          />
        </div>

        {isValidating && (
          <div className="validation-success">Checking current blackout and coverage rules...</div>
        )}

        {validationError && (
          <div className="validation-errors">{validationError}</div>
        )}

        {validationResult && !isValidating && (
          <>
            {normalizedValidationStatus === 'blocked by blackout' || normalizedValidationStatus === 'blocked by coverage' ? (
              <div className="validation-errors">
                <strong>Request blocked:</strong>
                {(validationResult.conflicts || []).map((conflict, index) => (
                  <div key={`${conflict.type}-${index}`} className={`conflict-item ${conflict.severity || ''}`}>
                    <strong>{conflict.type}:</strong> {conflict.message}
                  </div>
                ))}
              </div>
            ) : normalizedValidationStatus === 'review needed' ? (
              <div className="validation-warnings">
                <strong>Request requires review:</strong>
                {(validationResult.warnings || []).map((warning, index) => (
                  <div key={`warning-${index}`} className="warning-item">
                    {warning.message}
                  </div>
                ))}
              </div>
            ) : (
              <div className="validation-success">
                Request passed the current hard-stop rules and will enter the approval queue.
              </div>
            )}
          </>
        )}

        {submitError && (
          <div className="validation-errors">{submitError}</div>
        )}

        <div className="form-actions">
          <button type="submit" className="btn-submit" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </button>
          <button
            type="reset"
            className="btn-reset"
            onClick={() => {
              setFormData({
                startDate: '',
                endDate: '',
                timeBlock: 'FULL DAY',
                reason: ''
              });
              setValidationResult(null);
              setSubmitError('');
              setSubmitMessage('');
            }}
            disabled={isSubmitting}
          >
            Clear
          </button>
        </div>

        {submitMessage && (
          <div className="submission-success">
            {submitMessage}
          </div>
        )}
      </form>
    </div>
  );
}

export default VacationRequestForm;
